import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { notifyDefectComment } from "@/lib/notifications";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { logger } from "@/lib/logger";

export interface DefectComment {
  id: string;
  defect_id: string;
  user_id: string;
  content: string;
  created_at: string;
  user_profile?: {
    first_name: string | null;
    last_name: string | null;
    display_name: string | null;
    email: string;
    avatar_url?: string | null;
  } | null;
}

export const defectCommentsKeys = {
  all: ["defect-comments"] as const,
  byDefect: (defectId: string) => ["defect-comments", defectId] as const,
};

export function useDefectComments(defectId: string | undefined) {
  const { profile } = useAuth();
  const activeTenantId = profile?.active_tenant_id;
  const queryClient = useQueryClient();

  // Real-time subscription for comment updates (centralized push)
  useRealtimeSubscription(
    "defect_comments",
    () => {
      if (defectId) {
        queryClient.invalidateQueries({ queryKey: defectCommentsKeys.byDefect(defectId) });
      }
    },
    { enabled: !!defectId },
  );

  return useQuery({
    queryKey: defectCommentsKeys.byDefect(defectId || ""),
    queryFn: async (): Promise<DefectComment[]> => {
      if (!defectId) return [];

      const { data, error } = await supabase
        .from("defect_comments")
        .select(
          `
          id,
          defect_id,
          user_id,
          content,
          created_at,
          tenant_id
        `,
        )
        .eq("defect_id", defectId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Fetch user profiles separately
      const { data: profiles } = await supabase.rpc("list_tenant_profiles");

      const profileMap = new Map(profiles?.map((p) => [p.user_id, p]) || []);

      return (data || []).map((comment) => ({
        ...comment,
        user_profile: profileMap.get(comment.user_id) || null,
      }));
    },
    enabled: !!defectId && !!activeTenantId,
  });
}

interface AddDefectCommentParams {
  defectId: string;
  content: string;
  defectTitle: string;
  assignedToUserId?: string | null;
}

export function useAddDefectComment() {
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();
  const activeTenantId = profile?.active_tenant_id;

  const { t } = useTranslation();

  // Build commenter name from profile
  const getCommenterName = (): string => {
    if (profile?.display_name) return profile.display_name;
    if (profile?.first_name || profile?.last_name) {
      return [profile.first_name, profile.last_name].filter(Boolean).join(" ");
    }
    return profile?.email || "Jemand";
  };

  return useMutation({
    mutationFn: async ({ defectId, content }: AddDefectCommentParams) => {
      if (!user?.id || !activeTenantId) {
        throw new Error("User or tenant not available");
      }

      const { data, error } = await supabase
        .from("defect_comments")
        .insert({
          defect_id: defectId,
          content,
          user_id: user.id,
          tenant_id: activeTenantId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, { defectId, defectTitle, assignedToUserId }) => {
      queryClient.invalidateQueries({ queryKey: defectCommentsKeys.byDefect(defectId) });

      // Fire-and-forget: notify without blocking UI
      if (assignedToUserId && assignedToUserId !== user?.id) {
        const commenterName = getCommenterName();
        notifyDefectComment(assignedToUserId, defectTitle, defectId, commenterName)
          .then((sent) => {
            if (sent) {
              logger.info("Defect comment notification sent", { defectId });
            } else {
              logger.warn("Defect comment notification not sent", { defectId });
            }
          })
          .catch((err) =>
            logger.error("Defect comment notification threw", {
              defectId,
              name: err instanceof Error ? err.name : null,
            }),
          );
      }

      toast.success(t("defects.commentAdded"), {
        description: t("defects.commentAddedDesc"),
      });
    },
    onError: () => {
      toast.error(t("common.error"), {
        description: t("defects.commentError"),
      });
    },
  });
}
